import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import type { ServerState } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { commonPrefix, useConsoleCompletions } from './useConsoleCompletions';
import type { Completions } from '@mcpanel/core';

const MAX_LINES = 1000;

function lineClass(line: string): string {
  if (line.startsWith('> ')) return 'console-line input';
  if (/\b(ERROR|SEVERE|FATAL)\b/.test(line)) return 'console-line error';
  if (/\bWARN(ING)?\b/.test(line)) return 'console-line warn';
  return 'console-line';
}

export function Console({
  serverId,
  state,
  onStateChange,
}: {
  serverId: string;
  state: ServerState;
  onStateChange: (state: ServerState) => void;
}) {
  const { canOperate, notify } = useApp();
  const [lines, setLines] = useState<string[]>([]);
  const [command, setCommand] = useState('');
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [sending, setSending] = useState(false);
  const disabled = !canOperate || state !== 'running';
  const completion = useConsoleCompletions(serverId, command, !disabled, (error) => notify(errorMessage(error), true));
  const [cycle, setCycle] = useState<{ base: string; data: Completions; index: number }>();
  const menu = cycle?.data ?? completion.data;
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef(true);
  const onStateRef = useRef(onStateChange);
  onStateRef.current = onStateChange;
  useEffect(() => {
    setCycle(undefined);
    completion.setOpen(false);
    completion.setSelected(-1);
  }, [serverId, disabled]);

  useEffect(() => {
    let cancelled = false;
    void api
      .logs(serverId)
      .then((result) => {
        if (!cancelled) setLines(result.lines.slice(-MAX_LINES));
      })
      .catch(() => undefined);
    const close = api.openConsole(serverId, (event) => {
      if (event.type === 'line') setLines((current) => [...current, event.line].slice(-MAX_LINES));
      else if (event.type === 'state') onStateRef.current(event.state);
    });
    return () => {
      cancelled = true;
      close();
    };
  }, [serverId]);

  useEffect(() => {
    const box = boxRef.current;
    if (box && stickRef.current) box.scrollTop = box.scrollHeight;
  }, [lines]);

  const onScroll = () => {
    const box = boxRef.current;
    if (box) stickRef.current = box.scrollHeight - box.scrollTop - box.clientHeight < 40;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    completion.setOpen(false);
    setCycle(undefined);
    const text = command.trim().replace(/^\//, '');
    if (!text || sending) return;
    setSending(true);
    try {
      const result = await api.console(serverId, text);
      // RCON で送ったコマンドと返事はサーバーのログに残らないので、画面にだけ足す
      const reply = (result?.output ?? '')
        .replace(/§./g, '')
        .split('\n')
        .filter((line) => line.trim());
      setLines((current) => [...current, `> ${text}`, ...reply].slice(-MAX_LINES));
      setHistory((current) => [text, ...current.filter((c) => c !== text)].slice(0, 50));
      setHistoryIndex(-1);
      setCommand('');
      stickRef.current = true;
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setSending(false);
    }
  };

  // ログの欄をクリックしたら、そのまま打てるように入力行へフォーカスを移す（文字を選んでいるときは除く）
  const focusPrompt = () => {
    if (disabled || window.getSelection()?.toString()) return;
    inputRef.current?.focus();
  };
  const accept = (value: string, start: number) => {
    setCommand(command.slice(0, start) + value);
    completion.setOpen(false);
    completion.setSelected(-1);
    setCycle(undefined);
    inputRef.current?.focus();
  };
  const tabComplete = async (backwards: boolean) => {
    try {
      if (completion.open && cycle) {
        const count = cycle.data.candidates.length;
        const index = backwards ? (cycle.index <= 0 ? count - 1 : cycle.index - 1) : (cycle.index + 1) % count;
        setCommand(cycle.base + cycle.data.candidates[index]!.value);
        setCycle({ ...cycle, index });
        completion.setSelected(index);
        return;
      }
      const data = await completion.load();
      if (!data?.candidates.length) return;
      if (data.candidates.length === 1) return accept(data.candidates[0]!.value, data.start);
      if (!completion.open) {
        setCommand(command.slice(0, data.start) + commonPrefix(data.candidates.map((candidate) => candidate.value)));
        completion.setOpen(true);
        completion.setSelected(-1);
        setCycle({ base: command.slice(0, data.start), data, index: -1 });
      } else {
        const index = backwards ? data.candidates.length - 1 : 0;
        setCycle({ base: command.slice(0, data.start), data, index });
        setCommand(command.slice(0, data.start) + data.candidates[index]!.value);
        completion.setSelected(index);
      }
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'Tab') {
      event.preventDefault();
      void tabComplete(event.shiftKey);
      return;
    }
    if (completion.open) {
      if (['Escape', 'Enter', 'ArrowUp', 'ArrowDown'].includes(event.key)) event.preventDefault();
      if (event.key === 'Escape') {
        completion.setOpen(false);
        setCycle(undefined);
      }
      if (event.key === 'Enter' && menu?.candidates.length) {
        const candidate = menu.candidates[Math.max(0, completion.selected)]!;
        accept(candidate.value, menu.start);
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        const length = menu?.candidates.length ?? 0;
        if (length) {
          const index =
            event.key === 'ArrowUp' && completion.selected < 0
              ? length - 1
              : (completion.selected + (event.key === 'ArrowDown' ? 1 : length - 1) + length) % length;
          completion.setSelected(index);
          if (cycle) setCycle({ ...cycle, index });
        }
      }
      return;
    }
    if (event.key === 'ArrowUp' && history.length) {
      event.preventDefault();
      const next = Math.min(historyIndex + 1, history.length - 1);
      setHistoryIndex(next);
      setCommand(history[next] ?? '');
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = historyIndex - 1;
      setHistoryIndex(Math.max(next, -1));
      setCommand(next >= 0 ? (history[next] ?? '') : '');
    }
  };

  return (
    <div className="console">
      <div className="row">
        <a className="btn" href={`/api/v1/servers/${encodeURIComponent(serverId)}/logs/download?scope=latest`}>
          ログをダウンロード（今の起動）
        </a>
        <a className="btn" href={`/api/v1/servers/${encodeURIComponent(serverId)}/logs/download?scope=all`}>
          保存されているすべてのログ
        </a>
        <a
          className="btn"
          href={`/api/v1/servers/${encodeURIComponent(serverId)}/logs/download?scope=crash`}
          title="crash-reports/ のクラッシュレポートと、Java ごと落ちたときの記録（hs_err）をまとめて保存します"
        >
          クラッシュログ
        </a>
      </div>
      <div
        className="console-lines"
        ref={boxRef}
        onScroll={onScroll}
        onMouseUp={focusPrompt}
        role="log"
        aria-live="off"
      >
        {lines.length === 0 && <div className="faint">ログはまだありません。</div>}
        {lines.map((line, index) => (
          <div key={index} className={lineClass(line)}>
            {line}
          </div>
        ))}
      </div>
      <div className="console-controls">
        {completion.open && menu && (
          <div className="console-completions" role="listbox" aria-label="コマンド補完候補">
            {menu.candidates.map((candidate, index) => (
              <button
                key={candidate.value}
                type="button"
                role="option"
                id={`console-option-${index}`}
                aria-selected={completion.selected === index}
                // 押した瞬間に入力欄のフォーカスが外れて一覧が閉じないようにする
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => accept(candidate.value, menu.start)}
              >
                <code>{candidate.value}</code> <span className="faint small">{candidate.description}</span>
              </button>
            ))}
          </div>
        )}
        <form className="console-form console-prompt" onSubmit={(event) => void submit(event)}>
          <span className="console-prompt-mark" aria-hidden="true">
            {sending ? '…' : '>'}
          </span>
          <input
            id="console-command"
            ref={inputRef}
            value={command}
            onChange={(event) => {
              setCommand(event.target.value);
              completion.setSelected(-1);
              setCycle(undefined);
            }}
            onKeyDown={onKeyDown}
            // 入力欄から離れたら候補の一覧を閉じる。候補のクリックは onMouseDown で入力欄から離れないようにしている。
            onBlur={() => {
              completion.setOpen(false);
              setCycle(undefined);
            }}
            placeholder={
              !canOperate
                ? '閲覧権限ではコマンドを送れません'
                : state !== 'running'
                  ? 'サーバーが停止しています'
                  : 'コマンドを入力して Enter（Tab で補完）'
            }
            disabled={disabled}
            readOnly={sending}
            aria-label="コンソールコマンド"
            autoComplete="off"
            enterKeyHint="send"
            aria-expanded={completion.open}
            aria-activedescendant={
              completion.open && completion.selected >= 0 ? `console-option-${completion.selected}` : undefined
            }
            spellCheck={false}
          />
        </form>
      </div>
    </div>
  );
}
